import useClinicUser from 'shared/hooks/useClinicUser';
import {
  CallDirection,
  CallHistory,
  CallHistoryType,
  CallProvider,
  CallStatus,
  useCreateCallHistoryAndParticipantsMutation,
  useUpdateOneCallHistoryMutation,
} from 'shared/types/graphql';
import { getPhoneNumberE164 } from 'shared/utils';

interface UseCallHistoryProps {
  callProvider: CallProvider;
  callType: CallHistoryType;
}

export const useCallHistory = ({
  callProvider,
  callType,
}: UseCallHistoryProps): {
  createCallHistory: typeof createCallHistory;
  updateCallHistory: typeof updateCallHistory;
} => {
  const { currentClinicId } = useClinicUser();
  const [createCallHistoryAndParticipants] = useCreateCallHistoryAndParticipantsMutation();
  const [updateOneCallHistory] = useUpdateOneCallHistoryMutation();

  const createCallHistory = async ({
    sourceId,
    from,
    clinicPetParentPhoneNumber,
    clinicPetParentId,
    direction,
    status,
  }: {
    sourceId: string;
    from: string;
    clinicPetParentPhoneNumber?: string;
    clinicPetParentId?: string;
    direction: CallDirection;
    status?: CallStatus;
  }): Promise<Pick<CallHistory, 'id'> | null | undefined> => {
    const phoneNumberE164 = getPhoneNumberE164(from);

    if (!(sourceId && from && currentClinicId)) {
      console.error('Missing required data to create CallHistory');
    }

    const { data } = await createCallHistoryAndParticipants({
      variables: {
        data: {
          clinicId: currentClinicId,
          sourceId,
          clinicPhoneNumber: phoneNumberE164,
          clinicPetParentPhoneNumber,
          clinicPetParentId,
          provider: callProvider,
          type: callType,
          direction,
          status,
        },
      },
    });
    return data?.createCallHistory;
  };

  const updateCallHistory = async ({
    id,
    status,
    duration,
  }: {
    id: string | undefined;
    status?: CallStatus;
    duration?: number;
  }): Promise<void> => {
    if (!id) {
      console.error('Missing required `id` to update CallHistory');
    }
    await updateOneCallHistory({ variables: { where: { id }, data: { status, duration } } });
  };

  return { createCallHistory, updateCallHistory };
};
